<script>
  import { fade } from "svelte/transition";
  import { activateMicrophone } from "@lib/client/audio";
  import { playVideo } from "@lib/client/video";
  import * as analytics from "@services/analytics";
  import { _ } from "@services/i18n";
  import { hasAuthenticated, isPremiumStartIntent } from "@stores/auth";
  import { getConfiguration } from "@stores/component-configurations";
  import { featureEnabled } from "@stores/features";
  // eslint-disable-next-line max-len
  import { displaySuggestedQuestions, suggestedQuestions } from "@stores/features/suggested-questions";
  import {
    conversationVideo,
    experience,
    experienceType,
    introSeen,
    maximized,
    microphoneAvailable
  } from "@stores/global";
  import { showPaywall } from "@stores/sheets";
  import { toggleActiveVideo } from "@components/Video";
  import { EXPERIENCE_TYPES, FADE_DURATION } from "../consts";

  const {text: buttonText} = getConfiguration(
    "start-button",
    {
      text: $_("components.start_button.text", { default: "Start conversation" })
    }
  );

  /**
   * Set intro to seen after video played through
   *
   * If this is a premium start intent, and suggested questions feature
   * is active, display the suggested questions.
   *
   * If this is a premium experience, show the paywall.
   */
  function updateStoresAfterIntro() {
    introSeen.update(() => true);
    if ($isPremiumStartIntent && featureEnabled("suggested-questions") &&
      $suggestedQuestions &&
      $suggestedQuestions.length) {
      // eslint-disable-next-line max-nested-callbacks
      displaySuggestedQuestions.update(() => true);
    } else if (featureEnabled("paywall")) {
      showPaywall.update(() => true);
    }
  }

  /**
   * Maximize the experience
   *
   * On first run, prime the conversation video and request
   * microphone access
   */
  // eslint-disable-next-line max-lines-per-function
  async function open() {
    analytics.logButtonClick("start_button");
    window.parent.postMessage("speakeasy:maximize", "*");
    maximized.update(() => true);

    if (!$introSeen) {
      // If this is a paid experience, wait for authentication to
      // happen before continuing. This will ensure the correct
      // start intent video is loaded
      if (featureEnabled("paywall")) {
        await new Promise((resolve) => {
          if ($hasAuthenticated) {
            resolve();
            return;
          }

          let unsubscribe;
          // eslint-disable-next-line prefer-const
          unsubscribe = hasAuthenticated.subscribe((authed) => {
            if (authed) {
              if (typeof unsubscribe === "function") {
                unsubscribe();
              }

              resolve();
            }
          });
        });
      }

      // Prime the conversation video
      playVideo($conversationVideo).thenPause();
      // Request microphone
      try {
        await activateMicrophone();
        analytics.logGetUserMediaRequestSuccess();
        microphoneAvailable.set(true);
      } catch(error) {
        if (error.message === "Permission denied") {
          // eslint-disable-next-line no-alert
          alert(
            $_("components.start_button.notifications.permission_denied", {
              default: "Please allow microphone capture for the best experience"
            })
          );
        } else {
          if (error.message === "getUserMedia not supported") {
            // eslint-disable-next-line no-alert
            alert(
              $_(
                // eslint-disable-next-line max-len
                "components.start_button.notifications.getusermedia_not_supported",
                {
                  default: "Your device does not support microphone capture"
                }
              )
            );
          } else {
            // eslint-disable-next-line no-alert
            alert(
              $_(
                // eslint-disable-next-line max-len
                "components.start_button.notifications.getusermedia_generic",
                {
                  default: "We haven’t been able to activate your microphone"
                }
              )
            );
          }
        }

        analytics.logGetUserMediaRequestFailure();

        // Switch to text only mode
        experienceType.update(() => EXPERIENCE_TYPES.TEXT);
      }

      if ($experience.skipIntro) {
        updateStoresAfterIntro();
      } else {
        // Switch active video
        toggleActiveVideo().andPlay()
          .atEnd(() => {
            updateStoresAfterIntro();
            toggleActiveVideo().andPlay();
          });
      }
    }
  }
</script>

<style>
  .start {
    appearance: none;
    background: var(--primary-color);
    border: 0;
    border-radius: 0.25em;
    bottom: 2rem;
    color: var(--on-primary-color);
    cursor: pointer;
    font-size: 1.75rem;
    left: 50%;
    padding: 1.5rem 2.25rem;
    position: absolute;
    transform: translateX(-50%);
    z-index: 3;
  }
</style>

<button
  class="start"
  transition:fade={{ duration: FADE_DURATION }}
  type="button"
  on:click|preventDefault|stopPropagation={open}>
  <span>
    {buttonText}
  </span>
</button>
